import React, { useEffect, useState } from 'react';
import { Plus, Trash2, Eye, EyeOff } from 'lucide-react';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Button } from '../ui/button';
import { toast } from '../../hooks/use-toast';
import { API_BASE_URL } from '../../config';
import { apiClient } from '../../services/api';

const RUTA = `${API_BASE_URL}/api/admin/savings-equivalences`;
const pesos = (n) => `$${Number(n || 0).toLocaleString('es-MX', { maximumFractionDigits: 0 })}`;

/**
 * Módulo 6 — Editor de equivalencias de ahorro.
 *
 * Es lo que hace que «te ahorraste $5,000» signifique algo: la app lo traduce a
 * una cosa concreta que cuesta más o menos lo mismo.
 *
 * Cómo elige la app: toma al azar una de las equivalencias **cuyo precio cabe
 * dentro de lo ahorrado**. Por eso conviene tener varias repartidas en distintos
 * rangos — con solo cosas caras, quien lleva poco ahorro no vería ninguna.
 */
export default function SavingsEquivalences() {
  const [lista, setLista] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [nueva, setNueva] = useState({ label: '', amount: '', icon: '' });
  const [guardando, setGuardando] = useState(false);

  const cargar = async () => {
    try {
      const r = await apiClient.get(RUTA);
      setLista(r?.data ?? []);
    } catch (e) {
      toast({ title: 'Error', description: 'No se pudieron cargar las equivalencias', variant: 'destructive' });
    } finally {
      setCargando(false);
    }
  };

  useEffect(() => { cargar(); }, []);

  const agregar = async () => {
    if (!nueva.label.trim() || !(Number(nueva.amount) > 0)) {
      toast({ title: 'Faltan datos', description: 'Escribe con qué se compara y cuánto cuesta.', variant: 'destructive' });
      return;
    }
    setGuardando(true);
    try {
      await apiClient.post(RUTA, {
        label: nueva.label.trim(),
        amount: Number(nueva.amount),
        icon: nueva.icon.trim() || null,
      });
      setNueva({ label: '', amount: '', icon: '' });
      await cargar();
      toast({ title: 'Equivalencia agregada' });
    } catch (e) {
      toast({ title: 'Error', description: e.message || 'No se pudo guardar', variant: 'destructive' });
    } finally {
      setGuardando(false);
    }
  };

  const alternar = async (e) => {
    try {
      await apiClient.put(`${RUTA}/${e.id}`, { isActive: !e.isActive });
      await cargar();
    } catch (_) {
      toast({ title: 'Error', description: 'No se pudo actualizar', variant: 'destructive' });
    }
  };

  const borrar = async (e) => {
    if (!window.confirm(`¿Eliminar «${e.label}»?`)) return;
    try {
      await apiClient.delete(`${RUTA}/${e.id}`);
      await cargar();
      toast({ title: 'Equivalencia eliminada' });
    } catch (_) {
      toast({ title: 'Error', description: 'No se pudo eliminar', variant: 'destructive' });
    }
  };

  const activas = lista.filter((e) => e.isActive);
  const masBarata = activas.length ? Math.min(...activas.map((e) => e.amount)) : null;

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-3xl font-bold text-gray-900 mb-2">Equivalencias de ahorro</h2>
        <p className="text-gray-500 text-sm max-w-2xl">
          Con esto la app traduce el ahorro a algo que se entienda: «te ahorraste $5,000 —
          equivale a un vuelo a Cancún». Se muestra una al azar entre las que el ahorro
          alcanza a pagar, así que conviene tener varias en distintos rangos.
        </p>
      </div>

      {/* Aviso útil: sin opciones baratas, los usuarios nuevos no ven ninguna. */}
      {masBarata !== null && masBarata > 500 && (
        <div className="bg-amber-50 border border-amber-200 rounded-2xl px-4 py-3">
          <p className="text-amber-800 text-sm">
            La equivalencia más barata cuesta {pesos(masBarata)}. Quien lleve menos ahorro que
            eso no verá ninguna — vale la pena agregar alguna de monto bajo.
          </p>
        </div>
      )}

      {/* Alta */}
      <div className="border border-gray-200 rounded-2xl p-5 space-y-4">
        <Label className="text-gray-900">Agregar equivalencia</Label>
        <div className="grid grid-cols-1 sm:grid-cols-[1fr_160px_110px_auto] gap-3 items-end">
          <div>
            <Label className="text-gray-500 text-xs">¿Con qué se compara?</Label>
            <Input
              value={nueva.label}
              onChange={(e) => setNueva({ ...nueva, label: e.target.value })}
              placeholder="Un vuelo a Cancún"
              className="bg-gray-50 border-gray-300 rounded-xl h-11 mt-1"
            />
          </div>
          <div>
            <Label className="text-gray-500 text-xs">¿Cuánto cuesta?</Label>
            <Input
              type="number" min="1" step="1"
              value={nueva.amount}
              onChange={(e) => setNueva({ ...nueva, amount: e.target.value })}
              placeholder="4800"
              className="bg-gray-50 border-gray-300 rounded-xl h-11 mt-1"
            />
          </div>
          <div>
            <Label className="text-gray-500 text-xs">Emoji</Label>
            <Input
              value={nueva.icon}
              onChange={(e) => setNueva({ ...nueva, icon: e.target.value })}
              placeholder="✈️"
              className="bg-gray-50 border-gray-300 rounded-xl h-11 mt-1"
            />
          </div>
          <Button onClick={agregar} disabled={guardando} className="rounded-xl h-11">
            <Plus className="w-4 h-4 mr-1" /> Agregar
          </Button>
        </div>
      </div>

      {/* Lista */}
      {cargando ? (
        <p className="text-gray-500 text-sm">Cargando...</p>
      ) : lista.length === 0 ? (
        <p className="text-gray-500 text-sm">
          Todavía no hay equivalencias. Mientras no exista ninguna, la app solo muestra la
          equivalencia automática de acceso («2 años de acceso»).
        </p>
      ) : (
        <div className="border border-gray-200 rounded-2xl overflow-hidden">
          {lista.map((e, i) => (
            <div
              key={e.id}
              className={`flex items-center gap-3 px-4 py-3 ${i > 0 ? 'border-t border-gray-200' : ''} ${
                e.isActive ? '' : 'opacity-50'
              }`}
            >
              <span className="text-2xl w-8 text-center flex-none">{e.icon || '🎁'}</span>
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-gray-900 truncate">{e.label}</div>
                <div className="text-gray-500 text-xs">
                  Aparece cuando el ahorro llega a {pesos(e.amount)}
                </div>
              </div>
              <button
                onClick={() => alternar(e)}
                className="p-2 rounded-lg text-gray-500 hover:text-gray-900"
                title={e.isActive ? 'Ocultar de la app' : 'Mostrar en la app'}
              >
                {e.isActive ? <Eye size={17} /> : <EyeOff size={17} />}
              </button>
              <button
                onClick={() => borrar(e)}
                className="p-2 rounded-lg text-red-500 hover:text-red-700"
                title="Eliminar"
              >
                <Trash2 size={17} />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
